export default class DashboardQueryService {
  /**
   * @param {string} [apiBase=''] - API base URL
   * @param {string} [accessToken=''] - Bearer token
   */
  constructor(
    apiBase = '',
    accessToken = ''
  ) {
    this.apiBase = apiBase;
    this.accessToken = accessToken;
  }

  /**
   * Returns aggregated dashboard metrics for a salon over a date range.
   *
   * @async
   * @param {string} salonId - Target salon ID
   * @param {string} startDate - Range start (`YYYY-MM-DD`)
   * @param {string} endDate - Range end (`YYYY-MM-DD`; inclusive or per API contract)
   * @param {'day'|'week'|'month'|string} [granularity='day'] - Bucket size for usage series
   * @returns {Promise<{
   *   customerCount: number,
   *   granularity: string,
   *   usages: Array<{
   *     startDate: string,
   *     metrics: {
   *       sentStyleCount: number,
   *       customerCount: number,
   *       viewedStyleCount: number,
   *       addedToCartCount: number,
   *       purchaseCount: number,
   *       salesAmount: number,
   *     },
   *   }>,
   *   staffs: Array<{
   *     name: string,
   *     role: number,
   *     performance: {
   *       sentStyleCount: number,
   *       viewedStyleCount: number,
   *       favoritedStyleCount: number,
   *       addedToCartCount: number,
   *       purchasedCount: number,
   *       purchasedCustomerCount: number,
   *       salesAmount: number,
   *     },
   *   }>,
   * }>} Summary payload
   */
  async getSummary(
    salonId,
    startDate,
    endDate,
    granularity = 'day'
  ) {
    const query = new URLSearchParams({
      datestart: startDate,
      dateend: endDate,
      granularity,
    });

    const options = {}

    if (this.accessToken)
      options.headers = {
        'X-Authorization': `Bearer ${this.accessToken}`,
      };

    return fetch(
      `${this.apiBase}/dashboard/${salonId}/summary?${query}`,
      options
    )
      .then(async (response) => {
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();

        if (result.code)
          throw new Error(`${result.code}: ${result.message}`);

        // Summary API does not return favoritedStyleCount yet; default to 0 until implemented.
        if (Array.isArray(result.staffs)) {
          result.staffs = result.staffs.map((staff) => ({
            ...staff,
            performance: {
              ...staff.performance,
              favoritedStyleCount: staff.performance?.favoritedStyleCount ?? 0,
            },
          }));
        }

        return result;
      });
  }

  /**
   * Returns salon customers with engagement metrics for a date range.
   *
   * @async
   * @param {string} salonId - Target salon ID
   * @param {string} startDate - Range start (`YYYY-MM-DD`)
   * @param {string} endDate - Range end (`YYYY-MM-DD`; inclusive or per API contract)
   * @param {string|null} [customerId=null] - When set, narrows to that customer; otherwise all in range
   * @param {string} [sort='lastVisitedAt'] - Field name to sort by
   * @param {'asc'|'desc'} [order='desc'] - Sort direction
   * @param {number} [limit=100] - Maximum number of rows to return
   * @returns {Promise<Array<{
   *   customerId: string,
   *   stylistNames: string[],
   *   lastVisitedAt: string,
   *   hasPurchased: boolean,
   *   performance: {
   *     salesAmount: number,
   *     sentStyleCount: number,
   *     viewedStyleCount: number,
   *     favoriteCount: number,
   *     addToCartCount: number,
   *     purchaseCount: number,
   *   },
   * }>>} Customer rows with performance
   */
  async getCustomers(
    salonId,
    startDate,
    endDate,
    customerIds = null,
    sort = 'lastVisitedAt',
    order = 'desc',
    limit = 100,
  ) {
    const query = new URLSearchParams({
      datestart: startDate,
      dateend: endDate,
      sort,
      order,
      limit,
    });

    if (customerIds)
      query.set('customerIds', customerIds);

    const options = {}

    if (this.accessToken)
      options.headers = {
        'X-Authorization': `Bearer ${this.accessToken}`,
      };

    return fetch(
      `${this.apiBase}/dashboard/${salonId}/customers?${query}`,
      options
    )
      .then(async (response) => {
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();

        if (result.code)
          throw new Error(`${result.code}: ${result.message}`);

        return result;
      });
  }

  /**
   * Returns per-send treatment (style engagement) history for one customer in a date range.
   *
   * @async
   * @param {string} salonId - Target salon ID
   * @param {string} startDate - Range start (`YYYY-MM-DD`)
   * @param {string} endDate - Range end (`YYYY-MM-DD`; inclusive or per API contract)
   * @param {string} customerId - Customer to load history for
   * @returns {Promise<Array<{
   *   stylistNames: string[],
   *   sentAt: string,
   *   hasPurchased: boolean,
   *   salesAmount: number,
   *   viewed: boolean,
   *   favorited: boolean,
   *   addedToCart: boolean,
   *   purchased: boolean,
   * }>>} One row per sent style with engagement flags
   */
  async getTreatmentHistory(
    salonId,
    customerId,
    startDate,
    endDate,
  ) {
    const query = new URLSearchParams({
      datestart: startDate,
      dateend: endDate,
    });

    const options = {}

    if (this.accessToken)
      options.headers = {
        'X-Authorization': `Bearer ${this.accessToken}`,
      };

    return fetch(
      `${this.apiBase}/dashboard/${salonId}/customers/${customerId}/histories?${query}`,
      options
    )
      .then(async (response) => {
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();

        if (result.code)
          throw new Error(`${result.code}: ${result.message}`);

        return result;
      });
  }
}